import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { performanceAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Star, 
  Search, 
  Plus,
  TrendingUp,
  Target,
  Award,
  MessageSquare,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const PerformanceReviews = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    review_period: 'Q1 2026',
    overall_rating: 4,
    ratings: {
      productivity: 4,
      quality: 4,
      teamwork: 4,
      communication: 4,
      initiative: 4
    },
    strengths: '',
    improvements: '',
    goals: '',
    comments: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reviewsRes, employeesRes] = await Promise.all([
        performanceAPI.getAll(),
        employeeAPI.getAll()
      ]);
      setReviews(reviewsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Performance reviews fetch error:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async () => {
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }

    try {
      await performanceAPI.create({
        ...formData,
        strengths: formData.strengths.split(',').map(s => s.trim()).filter(Boolean),
        improvements: formData.improvements.split(',').map(s => s.trim()).filter(Boolean),
        goals: formData.goals.split(',').map(s => s.trim()).filter(Boolean)
      });
      toast.success('Performance review created!');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Create review error:', error);
      toast.error('Failed to create review');
    }
  };

  const getEmployeeInfo = (employeeId) => {
    return employees.find(e => e.employee_id === employeeId);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-emerald-600';
    if (rating >= 3.5) return 'text-blue-600';
    if (rating >= 2.5) return 'text-amber-600';
    return 'text-red-600';
  };

  const getRatingBadge = (rating) => {
    if (rating >= 4.5) return <Badge className="bg-emerald-100 text-emerald-700">Exceptional</Badge>;
    if (rating >= 3.5) return <Badge className="bg-blue-100 text-blue-700">Good</Badge>;
    if (rating >= 2.5) return <Badge className="bg-amber-100 text-amber-700">Satisfactory</Badge>;
    return <Badge className="bg-red-100 text-red-700">Needs Improvement</Badge>;
  };

  // Generate demo reviews if none exist
  const displayReviews = reviews.length > 0 ? reviews : employees.slice(0, 8).map((emp, idx) => ({
    id: `review-${idx}`,
    employee_id: emp.employee_id,
    reviewer_id: '200001',
    review_period: 'Q4 2025',
    overall_rating: 3 + Math.random() * 2,
    ratings: {
      productivity: 3 + Math.random() * 2,
      quality: 3 + Math.random() * 2,
      teamwork: 3 + Math.random() * 2,
      communication: 3 + Math.random() * 2,
      initiative: 3 + Math.random() * 2
    },
    strengths: ['Strong technical skills', 'Team player'],
    improvements: ['Time management'],
    goals: ['Complete certification', 'Lead project'],
    comments: 'Good performance overall.',
    status: idx % 3 === 0 ? 'draft' : 'submitted',
    created_at: new Date().toISOString()
  }));

  const filteredReviews = displayReviews.filter(review => {
    const emp = getEmployeeInfo(review.employee_id);
    const name = `${emp?.first_name} ${emp?.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-reviews-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Star className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Performance Reviews
                </h2>
                <p className="text-amber-100">
                  Manage employee performance evaluations
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-white text-amber-600 hover:bg-amber-50"
              data-testid="new-review-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Review
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Reviews</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayReviews.length}
                </p>
              </div>
              <Star className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Avg Rating</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {(displayReviews.reduce((sum, r) => sum + r.overall_rating, 0) / displayReviews.length || 0).toFixed(1)}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Top Performers</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayReviews.filter(r => r.overall_rating >= 4.5).length}
                </p>
              </div>
              <Award className="w-10 h-10 text-violet-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {displayReviews.filter(r => r.status === 'draft').length}
                </p>
              </div>
              <MessageSquare className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by employee name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="reviews-search"
        />
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredReviews.map((review) => {
          const emp = getEmployeeInfo(review.employee_id);
          return (
            <Card key={review.id} className="card-hover" data-testid={`review-card-${review.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={emp?.avatar} />
                      <AvatarFallback className="bg-amber-100 text-amber-600 font-medium">
                        {emp?.first_name?.[0]}{emp?.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {emp?.first_name} {emp?.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{emp?.position}</p>
                    </div>
                  </div>
                  {getRatingBadge(review.overall_rating)}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{review.review_period}</span>
                  </div>
                  <div className={`text-2xl font-bold ${getRatingColor(review.overall_rating)}`}>
                    {review.overall_rating.toFixed(1)}/5
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {Object.entries(review.ratings || {}).slice(0, 3).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 capitalize">{key}</span>
                        <span className="font-medium text-slate-900">{value.toFixed(1)}</span>
                      </div>
                      <Progress value={value * 20} className="h-1.5" />
                    </div>
                  ))}
                </div>

                {review.strengths && review.strengths.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">Strengths:</p>
                    <div className="flex flex-wrap gap-1">
                      {review.strengths.slice(0, 2).map((s, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Button variant="outline" className="w-full" size="sm">
                  View Full Review
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-12">
          <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Reviews Found</h3>
          <p className="text-slate-500">Try adjusting your search</p>
        </div>
      )}

      {/* New Review Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Performance Review</DialogTitle>
            <DialogDescription>Evaluate employee performance</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.role === 'employee').map(emp => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Review Period</Label>
                <Select 
                  value={formData.review_period}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, review_period: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1 2026">Q1 2026</SelectItem>
                    <SelectItem value="Q4 2025">Q4 2025</SelectItem>
                    <SelectItem value="Q3 2025">Q3 2025</SelectItem>
                    <SelectItem value="Annual 2025">Annual 2025</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Overall Rating: {formData.overall_rating}/5</Label>
              <input
                type="range"
                min="1"
                max="5"
                step="0.5"
                value={formData.overall_rating}
                onChange={(e) => setFormData(prev => ({ ...prev, overall_rating: parseFloat(e.target.value) }))}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label>Strengths (comma-separated)</Label>
              <Input
                placeholder="Leadership, Technical skills, Communication"
                value={formData.strengths}
                onChange={(e) => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Areas for Improvement (comma-separated)</Label>
              <Input
                placeholder="Time management, Documentation"
                value={formData.improvements}
                onChange={(e) => setFormData(prev => ({ ...prev, improvements: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Goals (comma-separated)</Label>
              <Input
                placeholder="Complete certification, Lead team project"
                value={formData.goals}
                onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Comments</Label>
              <Textarea
                placeholder="Overall feedback and comments..."
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateReview} className="bg-amber-600 hover:bg-amber-700">
              Create Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceReviews;
